import { useRef, useState } from "react";
import { PascalCase } from "../utils";

import Text from "./Text";
import { flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

const ROW_HEIGHT = 38;

const MachineTable = ({ table, theme}) => {

    const isDark = theme.White !== "#FFFFFF";
    const hoverColor = isDark ? theme.FOURFOUR : theme.Ey;
    const [hoveredRow, setHoveredRow] = useState(null);
    const getColor = (accessorKey) => {

        switch(accessorKey) {
            case 'maquina':
                return theme.ONED
            case 'hrsArregloReal':
            case 'hrsProdReal':
            case 'hrsImprodReal':
            case 'hrsImprodNoReportadas':
            case 'hrsMaquinaNoTripuladas':
            case 'hrsMaqRelojChecador':
            case 'eficienciaMaquinaPonderada':
            case 'costoProductivo':
            case 'costoImproductivo':
                return theme.SEVENB
            default:
                return theme.ONED
        }
    }

    const getColWidth = (key) => {
        switch(key) {
            case 'maquina':
                return {flex: '2 1 200px', minWidth: '100px'};
            case 'hrsImprodNoReportadas':
                return {flex: '1.5 1 165px', minWidth: '115px'}
            case 'eficienciaMaquinaPonderada':
                return {flex: '1.3 1 140px', minWidth: '90px'};
            default:
                return {flex: '1 1 100px', minWidth: '60px'};
        }
    }

    const styles = {
        row: {
            display: 'flex', width: '100%', minWidth: 'min-content', height: '38px',
            alignItems: 'center', boxSizing: 'border-box'
        },
        colWrapper: (key) =>  ({
            ...getColWidth(key), display: 'flex', alignItems: 'center', boxSizing: 'border-box',
            padding: '0 16px'
        }),
        truncate: {
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'
        }
    }

    const containerRef = useRef(null);
    const {rows} = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10
    })

    return (
        <div ref={containerRef} style={{width: '100%', minWidth: 0, height: '500px', overflowY: 'auto', overflowX: 'auto'}}>
            <div style={{minWidth: 'min-content', width: '100%', height: `${rowVirtualizer.getTotalSize() + ROW_HEIGHT}px`, position: 'relative'}}>
                <div style={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.Ey : theme.Ed}}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <div key={headerGroup.id} style={{...styles.row, height: '38px', border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}>
                            {headerGroup.headers.map((header, i) => {
                                const accessorKey = header.column.columnDef.accessorKey;

                                const isSorted = header.column.getIsSorted();
                                const sortIcon = isSorted === 'asc' ? ' ▲' : isSorted === 'desc' ? ' ▼' : '';
                                return (
                                    <div key = {header.id} onClick={header.column.getToggleSortingHandler()} style={{...styles.colWrapper(accessorKey, i), height: '36px', display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none'}}>
                                        {header.isPlaceHolder ? null : (
                                            <>
                                                <Text size={15} weigth={500} color={theme.ONED}>{PascalCase(header.column.columnDef.header, header.getContext())}</Text>
                                                <Text size={12} style={{marginLeft: '4px'}}>{sortIcon}</Text>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index];
                    return (
                        <div key={row.id} onMouseEnter={() => setHoveredRow(row.id)} onMouseLeave={() => setHoveredRow(null)} style={{...styles.row, position: 'absolute', top: ROW_HEIGHT, left: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, backgroundColor: hoveredRow === row.id ? hoverColor : isDark ? theme.Ey : theme.Ed, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}>
                            {row.getVisibleCells().map((cell, i) => {
                                const accessorKey = cell.column.columnDef.accessorKey;
                                const value = cell.getValue();
                                const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                                return (
                                    <div key={cell.id} style={{...styles.colWrapper(accessorKey, i)}} title={value}>
                                        <Text color={getColor(accessorKey)} size={15} style={styles.truncate}>
                                            {typeof value === 'string' ? PascalCase(value) : content}
                                        </Text>
                                    </div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
        </div>
    )
};

export default MachineTable;